import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

function joinClassNames(...values) {
  return values.filter(Boolean).join(" ");
}

export default function Menu({
  trigger,
  children,
  open,
  defaultOpen = false,
  onOpenChange,
  onClose,
  placement = "bottom-end",
  closeOnSelect = true,
  // Optional exit: keep the closed surface mounted (inert, `is-closing`) for
  // this many milliseconds so CSS can animate it out. 0 unmounts at once.
  exitDuration = 0,
  className = "",
  menuClassName = "",
  ...menuProps
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const generatedId = useId().replace(/:/g, "");
  const menuId = `lz-v2-menu-${generatedId}`;
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const stateRef = useRef(null);
  stateRef.current = { isControlled, onClose, onOpenChange };

  const setOpen = useCallback((nextOpen, reason) => {
    const current = stateRef.current;
    if (!current.isControlled) {
      setInternalOpen(nextOpen);
    }
    current.onOpenChange?.(nextOpen, reason);
    if (!nextOpen) {
      current.onClose?.(reason);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    menuRef.current?.querySelector('[role="menuitem"]:not([aria-disabled="true"])')?.focus();

    function handlePointerDown(event) {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false, "outside");
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false, "escape");
        rootRef.current?.querySelector(".lz-v2-menu__trigger")?.focus();
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, setOpen]);

  const [closing, setClosing] = useState(false);
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    setClosing(!isOpen && exitDuration > 0);
  }
  useEffect(() => {
    if (!closing) return undefined;
    const timer = window.setTimeout(() => setClosing(false), exitDuration);
    return () => window.clearTimeout(timer);
  }, [closing, exitDuration]);

  if (!isValidElement(trigger)) {
    throw new Error("Menu requires a single React element as its trigger.");
  }

  const triggerElement = cloneElement(trigger, {
    className: joinClassNames("lz-v2-menu__trigger", trigger.props.className),
    "aria-haspopup": "menu",
    "aria-expanded": isOpen,
    "aria-controls": menuId,
    onClick(event) {
      trigger.props.onClick?.(event);
      if (!event.defaultPrevented && !trigger.props.disabled) {
        setOpen(!isOpen, isOpen ? "trigger-close" : "trigger-open");
      }
    },
  });

  function handleMenuClick(event) {
    menuProps.onClick?.(event);
    const item = event.target.closest?.('[role="menuitem"]');
    if (
      closeOnSelect &&
      item &&
      item.getAttribute("aria-disabled") !== "true" &&
      !event.defaultPrevented
    ) {
      setOpen(false, "selection");
      rootRef.current?.querySelector(".lz-v2-menu__trigger")?.focus();
    }
  }

  return (
    <span ref={rootRef} className={joinClassNames("lz-v2-menu", className)}>
      {triggerElement}
      {isOpen || closing ? (
        <div
          {...menuProps}
          ref={menuRef}
          className={joinClassNames(
            "lz-v2-menu__surface",
            `lz-v2-menu__surface--${placement}`,
            !isOpen && "is-closing",
            menuClassName
          )}
          id={menuId}
          role="menu"
          aria-hidden={!isOpen || undefined}
          inert={!isOpen ? "" : undefined}
          onClick={isOpen ? handleMenuClick : undefined}
        >
          {children}
        </div>
      ) : null}
    </span>
  );
}
